import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_quiz-completion.scss';

const quizQuestions = [
  {
    question: "What is work-life integration?",
    options: [
      "Separating work and life completely",
      "Blending work and life in a way that supports both",
      "Working all the time",
      "Ignoring personal priorities"
    ],
    correctAnswer: 1,
    explanation: "Work-life integration means blending work and life in a way that supports both."
  },
  {
    question: "Which method helps you pause and reset for balance?",
    options: [
      "STOP Method: Stop, Take a breath, Observe, Proceed mindfully",
      "Work nonstop",
      "Never take breaks",
      "Always say yes"
    ],
    correctAnswer: 0,
    explanation: "The STOP Method helps you pause and reset for balance."
  },
  {
    question: "Occupational wellness is about:",
    options: [
      "Perfection",
      "Maintaining energy, purpose, and balance over time",
      "Working harder than everyone else",
      "Ignoring your needs"
    ],
    correctAnswer: 1,
    explanation: "Occupational wellness is about maintaining energy, purpose, and balance over time."
  },
  {
    question: "How can you find more purpose in your work?",
    options: [
      "Reflect on what you value and enjoy",
      "Ignore achievements",
      "Work nonstop",
      "Never celebrate progress"
    ],
    correctAnswer: 0,
    explanation: "Reflecting on what you value and enjoy helps you find more purpose in your work."
  },
  {
    question: "Designing work-life harmony can help you:",
    options: [
      "Feel more balanced and fulfilled",
      "Become more stressed",
      "Work longer hours",
      "Ignore your needs"
    ],
    correctAnswer: 0,
    explanation: "Designing work-life harmony helps you feel more balanced and fulfilled."
  }
];

const OccupationalModule5QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { saveProgress } = useModuleProgress('occupationalM5');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (index: number) => {
    if (selectedAnswer === null) {
      setSelectedAnswer(index);
      setShowExplanation(true);
    }
  };

  const handleNext = async () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
  await saveProgress(quizQuestions.length, true); // Mark module as completed
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">You've completed the Module 5 quiz!</p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <ul className="achievement-list">
              <li><span className="check"></span> Understanding work-life integration</li>
              <li><span className="check"></span> Applying the STOP Method</li>
              <li><span className="check"></span> Maintaining energy, purpose, and balance</li>
            </ul>
          </div>
          <button className="resource-button" onClick={() => navigate('/panel/learn/occupational-resources')}>Return to Resources</button>
        </div>
      </div>
    );
  }

  const currentQuiz = quizQuestions[currentQuestion];
  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left occupational-panel">
          <div className="module-header">
            <h1>Module 5 Quiz</h1>
            <p className="module-subtitle">Designing Work-Life Harmony and Purpose</p>
            <hr className="module-divider" />
            <p className="module-tool-info">Test your understanding of work-life harmony and purpose.</p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${((currentQuestion + 1) / quizQuestions.length) * 100}%` }}></div>
              </div>
              <span className="progress-text">Question {currentQuestion + 1} of {quizQuestions.length}</span>
            </div>
            <div className="question-section">
              <h2 className="question-title">{currentQuiz.question}</h2>
              <div className="answer-options">
                {currentQuiz.options.map((option, idx) => (
                  <label key={idx} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={idx}
                      checked={selectedAnswer === idx}
                      onChange={() => handleAnswerSelect(idx)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button className="btn-secondary" onClick={currentQuestion === 0 ? () => navigate('/panel/learn/occupational-resources') : handleBack}>
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button className="btn-primary" onClick={handleNext} disabled={selectedAnswer === null}>Next Question</button>
              ) : (
                <button className="btn-primary" onClick={handleNext} disabled={selectedAnswer === null}>Complete Quiz</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupationalModule5QuizPage;


